import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Info, TriangleAlert } from 'lucide-react';
import { cx } from './kit';

type ToastTone = 'success' | 'info' | 'danger';
interface ToastItem {
  id: number;
  tone: ToastTone;
  text: string;
}

const Ctx = createContext<{ show(text: string, tone?: ToastTone): void } | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const show = useCallback((text: string, tone: ToastTone = 'success') => {
    const id = ++seq.current;
    setItems((xs) => [...xs.slice(-2), { id, tone, text }]);
    window.setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), tone === 'danger' ? 5200 : 3200);
  }, []);
  const value = useMemo(() => ({ show }), [show]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex flex-col items-center gap-2 px-4 pt-safe" aria-live="polite">
          <div className="h-2" />
          {items.map((t) => (
            <div
              key={t.id}
              className={cx(
                'pointer-events-auto flex w-full max-w-md animate-rise items-center gap-3 rounded-2xl px-4 py-3 text-[14px] font-semibold shadow-float',
                t.tone === 'danger' ? 'bg-danger text-white' : 'bg-ink text-bg',
              )}
            >
              {t.tone === 'success' ? <CheckCircle2 className="size-5 shrink-0 text-gold-bright" /> : t.tone === 'danger' ? <TriangleAlert className="size-5 shrink-0" /> : <Info className="size-5 shrink-0" />}
              <span className="min-w-0 flex-1">{t.text}</span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </Ctx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast ToastProvider içinde kullanılmalı');
  return ctx;
}
